import {createReducer, on} from '@ngrx/store';
import {
  initialStreamTranscodingConfigurationsState,
  StreamTranscodingConfigurationsState,
  streamTranscodingConfigurationsAdapter
} from './stream-transcoding-configurations.state';
import * as Actions from './stream-transcoding-configurations.actions';

export const streamTranscodingConfigurationsReducer = createReducer(
  initialStreamTranscodingConfigurationsState,

  // Load all
  on(Actions.loadStreamTranscodingConfigurations, (state: StreamTranscodingConfigurationsState): StreamTranscodingConfigurationsState => ({
    ...state,
    loading: true,
    error: null
  })),

  on(Actions.loadStreamTranscodingConfigurationsSuccess, (state: StreamTranscodingConfigurationsState, {items}): StreamTranscodingConfigurationsState => {
      return streamTranscodingConfigurationsAdapter.setAll(items, {
        ...state,
        loading: false,
        loaded: true,
        lastUpdated: Date.now(),
        error: null
      });
    }
  ),

  on(Actions.loadStreamTranscodingConfigurationsFailure, (state: StreamTranscodingConfigurationsState, {error}): StreamTranscodingConfigurationsState => ({
    ...state,
    loading: false,
    error
  })),

  // Create
  on(Actions.createStreamTranscodingConfiguration, (state: StreamTranscodingConfigurationsState): StreamTranscodingConfigurationsState => ({
    ...state,
    creating: true,
    createError: null
  })),

  on(Actions.createStreamTranscodingConfigurationSuccess, (state: StreamTranscodingConfigurationsState, {item}): StreamTranscodingConfigurationsState =>
    streamTranscodingConfigurationsAdapter.addOne(item, {
      ...state,
      creating: false,
      createError: null,
      selectedId: item.id
    })
  ),

  on(Actions.createStreamTranscodingConfigurationFailure, (state: StreamTranscodingConfigurationsState, {error}): StreamTranscodingConfigurationsState => ({
    ...state,
    creating: false,
    createError: error
  })),

  // Update
  on(Actions.updateStreamTranscodingConfiguration, (state: StreamTranscodingConfigurationsState, {id}): StreamTranscodingConfigurationsState => ({
    ...state,
    updating: {
      ...state.updating,
      [id]: true
    },
    updateError: {
      ...state.updateError,
      [id]: null
    }
  })),

  on(Actions.updateStreamTranscodingConfigurationSuccess, (state: StreamTranscodingConfigurationsState, {item}): StreamTranscodingConfigurationsState =>
    streamTranscodingConfigurationsAdapter.updateOne({id: item.id, changes: item}, {
      ...state,
      updating: {
        ...state.updating,
        [item.id]: false
      },
      updateError: {
        ...state.updateError,
        [item.id]: null
      }
    })
  ),

  on(Actions.updateStreamTranscodingConfigurationFailure, (state: StreamTranscodingConfigurationsState, {id, error}): StreamTranscodingConfigurationsState => ({
    ...state,
    updating: {
      ...state.updating,
      [id]: false
    },
    updateError: {
      ...state.updateError,
      [id]: error
    }
  })),

  // Resume
  on(Actions.resumeStreamTranscodingConfiguration, (state: StreamTranscodingConfigurationsState, {id}): StreamTranscodingConfigurationsState => ({
    ...state,
    updating: {
      ...state.updating,
      [id]: true
    },
    updateError: {
      ...state.updateError,
      [id]: null
    }
  })),

  on(Actions.resumeStreamTranscodingConfigurationSuccess, (state: StreamTranscodingConfigurationsState, {item}): StreamTranscodingConfigurationsState =>
    streamTranscodingConfigurationsAdapter.updateOne({id: item.id, changes: item}, {
      ...state,
      updating: {
        ...state.updating,
        [item.id]: false
      },
      updateError: {
        ...state.updateError,
        [item.id]: null
      }
    })
  ),

  on(Actions.resumeStreamTranscodingConfigurationFailure, (state: StreamTranscodingConfigurationsState, {id, error}): StreamTranscodingConfigurationsState => ({
    ...state,
    updating: {
      ...state.updating,
      [id]: false
    },
    updateError: {
      ...state.updateError,
      [id]: error
    }
  })),

  // Stop
  on(Actions.stopStreamTranscodingConfiguration, (state: StreamTranscodingConfigurationsState, {id}): StreamTranscodingConfigurationsState => ({
    ...state,
    updating: {
      ...state.updating,
      [id]: true
    },
    updateError: {
      ...state.updateError,
      [id]: null
    }
  })),

  on(Actions.stopStreamTranscodingConfigurationSuccess, (state: StreamTranscodingConfigurationsState, {item}): StreamTranscodingConfigurationsState =>
    streamTranscodingConfigurationsAdapter.updateOne({id: item.id, changes: item}, {
      ...state,
      updating: {
        ...state.updating,
        [item.id]: false
      },
      updateError: {
        ...state.updateError,
        [item.id]: null
      }
    })
  ),

  on(Actions.stopStreamTranscodingConfigurationFailure, (state: StreamTranscodingConfigurationsState, {id, error}): StreamTranscodingConfigurationsState => ({
    ...state,
    updating: {
      ...state.updating,
      [id]: false
    },
    updateError: {
      ...state.updateError,
      [id]: error
    }
  })),

  // Delete
  on(Actions.deleteStreamTranscodingConfiguration, (state: StreamTranscodingConfigurationsState, {id}): StreamTranscodingConfigurationsState => ({
    ...state,
    deleting: {
      ...state.deleting,
      [id]: true
    },
    deleteError: {
      ...state.deleteError,
      [id]: null
    }
  })),

  on(Actions.deleteStreamTranscodingConfigurationSuccess, (state: StreamTranscodingConfigurationsState, {id}): StreamTranscodingConfigurationsState => {
    const newState: StreamTranscodingConfigurationsState = streamTranscodingConfigurationsAdapter.removeOne(id, state);

    const { [id]: removedDeleting, ...restDeleting } = newState.deleting;
    const { [id]: removedDeleteError, ...restDeleteError } = newState.deleteError;
    const { [id]: removedUpdating, ...restUpdating } = newState.updating;
    const { [id]: removedUpdateError, ...restUpdateError } = newState.updateError;

    return {
      ...newState,
      deleting: restDeleting,
      deleteError: restDeleteError,
      updating: restUpdating,
      updateError: restUpdateError,
      selectedId: newState.selectedId === id ? null : newState.selectedId
    };
  }),

  on(Actions.deleteStreamTranscodingConfigurationFailure, (state: StreamTranscodingConfigurationsState, {id, error}): StreamTranscodingConfigurationsState => ({
    ...state,
    deleting: {
      ...state.deleting,
      [id]: false
    },
    deleteError: {
      ...state.deleteError,
      [id]: error
    }
  })),

  // UI
  on(Actions.selectStreamTranscodingConfiguration, (state: StreamTranscodingConfigurationsState, {id}): StreamTranscodingConfigurationsState => ({
    ...state,
    selectedId: id
  })),

  on(Actions.clearStreamTranscodingConfigurationErrors, (state: StreamTranscodingConfigurationsState): StreamTranscodingConfigurationsState => ({
    ...state,
    error: null,
    createError: null,
    updateError: {},
    deleteError: {}
  })),

  on(Actions.resetStreamTranscodingConfigurationsState, (): StreamTranscodingConfigurationsState => initialStreamTranscodingConfigurationsState)
);

